import { getAttemptScores, getEvaluationMark, getQuestionEntry, flattenExamQuestions } from '../../utils/examScores';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { collection, query, where, getDocs, doc, updateDoc, documentId, deleteDoc, runTransaction } from '../../lib/auditedFirestore';
import { evaluateExamAnswers, withEvaluationTimeout } from '../../services/examEvaluation';
import { db } from '../../lib/firebase';
import Spinner from '../../components/Spinner';
import ExamResult from '../exam/ExamResult';
import StaticSpreadsheetViewer from '../../components/editors/StaticSpreadsheetViewer';

// Do not overwrite another tutor's completed review while this browser was marking.
const stableReviewValue = value => value && typeof value === 'object'
    ? Array.isArray(value) ? value.map(stableReviewValue) : Object.fromEntries(Object.keys(value).sort().map(key=>[key,stableReviewValue(value[key])]))
    : value;
const reviewRevision = attempt => JSON.stringify(stableReviewValue({
    answers:attempt.answers || {},evaluations:attempt.evaluations || {},adminFeedback:attempt.adminFeedback || {},
    score:attempt.score ?? null,resubmissionStatus:attempt.resubmissionStatus || null,questionsUrl:attempt.questionsUrl || null,
}));
async function saveReviewedAttempt(attempt,changes) {
    const revision=reviewRevision(attempt),ref=doc(db,'attempts',attempt.id);
    await withEvaluationTimeout(runTransaction(db,async transaction=>{
        const snapshot=await transaction.get(ref);
        if(!snapshot.exists())throw new Error('This submission is no longer available.');
        if(reviewRevision(snapshot.data())!==revision)throw new Error('This submission was updated by another review. Existing marks have been kept. Refresh the submissions before trying again.');
        transaction.update(ref,changes);
    }),30000,'The updated result could not be confirmed as saved. Check the connection and refresh before retrying.');
}

// --- COMPONENTS ---

const ToppersModal = ({ toppers, onClose, examTitle }) => (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50">
        <div className="bg-white p-8 rounded-xl shadow-2xl max-w-md w-full text-center relative overflow-hidden animate-fade-in-up">
            {/* Confetti-like background decoration */}
            <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-yellow-300 via-yellow-500 to-yellow-300"></div>
            
            <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-yellow-500 to-orange-500 mb-2">
                🎉 Hall of Fame 🎉
            </h2>
            <p className="text-gray-500 mb-6 font-medium">Top Performers for {examTitle}</p>

            <div className="space-y-4">
                {toppers && toppers.length > 0 ? toppers.map((student, index) => {
                    let styleClass = "bg-white border-gray-200";
                    let icon = "🏅";
                    let rankLabel = `#${index + 1}`;
                    let textClass = "text-gray-700";

                    if (index === 0) { 
                        styleClass = "bg-yellow-50 border-yellow-400 shadow-lg scale-105 z-10"; 
                        icon = "🥇"; 
                        textClass = "text-yellow-800";
                        rankLabel = "1st";
                    } else if (index === 1) { 
                        styleClass = "bg-gray-50 border-gray-300"; 
                        icon = "🥈"; 
                        rankLabel = "2nd";
                    } else if (index === 2) { 
                        styleClass = "bg-orange-50 border-orange-200"; 
                        icon = "🥉"; 
                        rankLabel = "3rd";
                    }

                    return (
                        <div key={index} className={`flex items-center p-4 rounded-lg border-2 ${styleClass} transition-transform`}>
                            <div className="text-4xl mr-4 filter drop-shadow-sm">{icon}</div>
                            <div className="text-left flex-grow">
                                <p className={`font-bold text-lg ${textClass}`}>{student.name}</p>
                                <p className="text-xs text-gray-500 font-mono">Score: {student.score}</p>
                            </div>
                            <div className="font-black text-xl text-gray-300 italic">
                                {rankLabel}
                            </div>
                        </div>
                    );
                }) : (
                    <p className="text-gray-500">No toppers declared yet.</p>
                )}
            </div>

            <button
                onClick={onClose}
                className="mt-8 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-2 rounded-full hover:from-purple-700 hover:to-pink-700 transition shadow-lg font-bold"
            >
                Close
            </button>
        </div>
    </div>
);

const FeedbackModal = ({ attempt, exam, studentName, onClose, onSave, questions }) => {
    // Initialize feedback state with existing feedback or empty strings
    const [feedbackMap, setFeedbackMap] = useState(attempt.adminFeedback || {});
    const [isSaving, setIsSaving] = useState(false);
    const [manualMarks, setManualMarks] = useState({});
    const [saveError, setSaveError] = useState('');
    const [questionsToRender, setQuestionsToRender] = useState([]);
    const [loadingQuestions, setLoadingQuestions] = useState(true);
    useEffect(() => {
        let active=true;
        const load=async()=>{
            try {
                let saved=attempt.examQuestions;
                const url=attempt.questionsUrl || exam.publishedQuestionsUrl || exam.questionsUrl;
                if (!saved?.length && url) saved=await withEvaluationTimeout((async()=>{
                    const response=await fetch(url);
                    if(!response.ok)throw new Error('The saved question version could not be loaded.');
                    return response.json();
                })(),30000,'The saved question version could not be loaded. Please reopen the review.');
                if (!saved?.length && !url) saved=questions || exam.questions || [];
                if (!Array.isArray(saved) || !saved.length)throw new Error('The saved question version is unavailable.');
                if(active)setQuestionsToRender(saved);
            } catch(error) {if(active)setSaveError(error.message);}
            finally {if(active)setLoadingQuestions(false);}
        };
        load();return()=>{active=false;};
    },[attempt,exam,questions]);

    const handleFeedbackChange = (questionIndex, text) => {
        setFeedbackMap(prev => ({
            ...prev,
            [questionIndex]: text
        }));
    };

    const handleSave = async () => {
        if(isSaving || loadingQuestions || !questionsToRender.length)return;
        setIsSaving(true);setSaveError('');
        try {
            let evaluations;
            if(Object.keys(manualMarks).length) {
                evaluations={};
                for(const {question,index,subIndex,key} of flattenExamQuestions(questionsToRender)) {
                    if(question.type!=='descriptive')continue;
                    const old=getQuestionEntry(attempt.evaluations,index,subIndex,question.id);
                    let evaluation=old || {mark:null,maxMarks:Number(question.marks),status:'needs-review',text:'Evaluation pending.'};
                    if(Object.prototype.hasOwnProperty.call(manualMarks,key)) {
                        const value=manualMarks[key],mark=Number(value),maximum=Number(question.marks);
                        if(value.trim()==='' || !Number.isFinite(mark) || !Number.isFinite(maximum) || maximum<0 || mark<0 || mark>maximum)throw new Error(`Enter a mark from 0 to ${maximum} for question ${index+1}${subIndex===null?'':'.'+(subIndex+1)}.`);
                        evaluation={...evaluation,mark:Math.round(mark*100)/100,maxMarks:maximum,method:'manual',status:'graded'};
                    }
                    if(subIndex===null)evaluations[index]=evaluation;
                    else {if(!evaluations[index])evaluations[index]={};evaluations[index][subIndex]=evaluation;}
                }
            }
            await onSave(attempt.id, feedbackMap, evaluations);
            onClose();
        } catch(error) {setSaveError(error.message);}
        finally {setIsSaving(false);}
    };

    const renderManualMark = (question,index,subIndex,evaluation) => {
        const key=subIndex===null?String(index):`${index}-${subIndex}`;
        return <label className="mt-3 flex items-center gap-3 text-sm font-semibold text-purple-800">
            Tutor mark
            <input type="number" min="0" max={Number(question.marks)} step="0.01"
                aria-label={`Tutor mark for question ${index+1}${subIndex===null?'':'.'+(subIndex+1)}`}
                value={manualMarks[key] ?? getEvaluationMark(evaluation) ?? ''}
                onChange={event=>setManualMarks(previous=>({...previous,[key]:event.target.value}))}
                className="w-24 rounded border border-purple-200 p-2 bg-white" />
            <span>/ {question.marks}</span>
        </label>;
    };

    const renderStudentAnswer = (ans) => {
        if (ans?.wordContent || ans?.excelData) {
            return <div className="space-y-3">
                {ans.wordContent && <div className="text-sm text-gray-800 prose max-w-none bg-white p-3 rounded border border-blue-200 overflow-x-auto" dangerouslySetInnerHTML={{__html:ans.wordContent}} />}
                {ans.excelData && <StaticSpreadsheetViewer data={ans.excelData} />}
            </div>;
        }
        if (Array.isArray(ans)) {
            return <div className="text-sm text-gray-800 font-mono bg-white p-3 rounded border border-blue-200 whitespace-pre-wrap">{ans.join(', ')}</div>;
        }
        if (typeof ans === 'object' && ans !== null) {
            return <div className="text-sm text-gray-800 font-mono bg-white p-3 rounded border border-blue-200 whitespace-pre-wrap">{JSON.stringify(ans)}</div>;
        }
        return (
            <div className="text-sm text-gray-800 font-mono bg-white p-3 rounded border border-blue-200 whitespace-pre-wrap">
                {ans !== undefined && ans !== null && ans !== '' ? String(ans) : "No answer provided."}
            </div>
        );
    };


    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center bg-gray-50 rounded-t-xl">
                    <div>
                        <h2 className="text-xl font-bold text-gray-800">Admin Grading & Manual Feedback</h2>
                        <p className="text-sm text-gray-600">Reviewing Attempt for: <span className="font-semibold text-purple-700">{studentName}</span></p>
                    </div>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
                </div>

                <div className="p-6 overflow-y-auto flex-1 space-y-8 bg-gray-50">
                    {questionsToRender.length > 0 ? questionsToRender.map((q, index) => {
                        const studentAnswer = getQuestionEntry(attempt.answers, index, null, q.id);
                        const aiEval = getQuestionEntry(attempt.evaluations, index, null, q.id);
                        const currentFeedback = feedbackMap[index] || '';

                        return (
                            <div key={index} className="border border-gray-200 rounded-xl p-5 bg-white shadow-sm">
                                {/* Question Header & Text */}
                                <div className="mb-4 pb-3 border-b border-gray-100">
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="bg-purple-100 text-purple-700 text-xs font-bold px-2 py-1 rounded inline-block">QUESTION {index + 1}</span>
                                        <span className="text-sm font-bold text-gray-500">{q.marks} Marks</span>
                                    </div>
                                    <div 
                                        className="text-gray-800 font-semibold prose max-w-none" 
                                        dangerouslySetInnerHTML={{ __html: q.text || q.questionText }} 
                                    />
                                </div>

                                {/* --- FIX: Specific UI handling for Case Study Sub-Questions --- */}
                                {q.type === 'case-study' ? (
                                    <div className="space-y-4 mb-4">
                                        {q.subQuestions && q.subQuestions.map((subQ, subIdx) => {
                                            const subAns = getQuestionEntry(attempt.answers, index, subIdx, subQ.id);
                                            const subEval = getQuestionEntry(attempt.evaluations, index, subIdx, subQ.id);
                                            
                                            return (
                                                <div key={subIdx} className="ml-2 md:ml-4 pl-4 border-l-4 border-blue-200">
                                                    <p className="font-bold text-sm text-gray-700 mb-1">Sub-Question {subIdx + 1} ({subQ.marks} Marks)</p>
                                                    <div className="text-sm text-gray-800 mb-2 prose max-w-none" dangerouslySetInnerHTML={{__html: subQ.text}} />
                                                    
                                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-100 mb-2">
                                                         <p className="text-xs font-bold text-blue-800 mb-2 uppercase tracking-wide">Student Answer</p>
                                                         {renderStudentAnswer(subAns)}
                                                    </div>

                                                    {subQ.type === 'descriptive' && (
                                                         <div className="bg-green-50 p-3 rounded-lg border border-green-100 mb-2">
                                                             <div className="flex justify-between items-center mb-1">
                                                                 <p className="text-xs font-bold text-green-800 uppercase tracking-wide">Marking feedback</p>
                                                                 {subEval && <span className="text-xs font-bold bg-green-200 text-green-800 px-2 rounded">{getEvaluationMark(subEval) ?? 'Awaiting review'} Marks</span>}
                                                             </div>
                                                             <p className="text-sm text-gray-700 whitespace-pre-wrap italic">
                                                                 {subEval ? subEval.text : "AI evaluation not triggered."}
                                                             </p>
                                                             {renderManualMark(subQ,index,subIdx,subEval)}
                                                         </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                        {/* Single Question Student Answer Section */}
                                        <div className="bg-blue-50 p-4 rounded-lg border border-blue-100">
                                            <p className="text-xs font-bold text-blue-800 mb-2 uppercase tracking-wide">Student's Submitted Answer</p>
                                            {renderStudentAnswer(studentAnswer)}
                                        </div>

                                        {/* Single Question AI Evaluation Section */}
                                        {q.type === 'descriptive' && (
                                            <div className="bg-green-50 p-3 rounded-lg border border-green-100">
                                                <div className="flex justify-between items-center mb-1">
                                                    <p className="text-xs font-bold text-green-800 uppercase tracking-wide">Marking feedback</p>
                                                    {aiEval && <span className="text-xs font-bold bg-green-200 text-green-800 px-2 rounded">{getEvaluationMark(aiEval) ?? 'Awaiting review'} Marks</span>}
                                                </div>
                                                <p className="text-sm text-gray-700 whitespace-pre-wrap italic">
                                                    {aiEval ? aiEval.text : "AI evaluation not triggered for this question."}
                                                </p>
                                                {renderManualMark(q,index,null,aiEval)}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Manual Feedback Section (Applies generally to the whole block) */}
                                <div className="bg-orange-50 p-3 rounded-lg border border-orange-100 mt-4">
                                    <p className="text-xs font-bold text-orange-800 mb-2 uppercase tracking-wide">✍️ Your Overall Feedback (Visible to Student)</p>
                                    <textarea
                                        value={currentFeedback}
                                        onChange={(e) => handleFeedbackChange(index, e.target.value)}
                                        placeholder="Add constructive comments for the student regarding this question block..."
                                        className="w-full text-sm p-3 border border-orange-200 rounded-lg focus:ring-2 focus:ring-orange-400 focus:outline-none h-24 resize-none shadow-sm"
                                    />
                                </div>
                            </div>
                        );
                    }) : (
                        <div className="text-center py-10 text-gray-500">
                            <p>Loading questions data or no questions found...</p>
                        </div>
                    )}
                </div>

                {saveError && <p role="alert" className="mx-6 mb-3 rounded bg-red-50 p-3 text-red-700">{saveError}</p>}
                <div className="p-6 border-t border-gray-200 bg-gray-50 rounded-b-xl flex justify-end gap-3">
                    <button 
                        onClick={onClose}
                        className="px-5 py-2 text-gray-600 hover:bg-gray-200 rounded-lg font-medium transition"
                    >
                        Cancel
                    </button>
                    <button 
                        onClick={handleSave}
                        disabled={isSaving || loadingQuestions || !questionsToRender.length}
                        className="px-8 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg font-bold shadow-lg hover:shadow-xl transition disabled:opacity-50"
                    >
                        {isSaving ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Saving...
                            </>
                        ) : 'Save & Close'}
                    </button>
                </div>
            </div>
        </div>
    );
};

const ExamAttemptsList = ({ exam, paper, onBack, setModal, onAllowRetake }) => {
    const [attempts, setAttempts] = useState([]);
    const [students, setStudents] = useState({}); 
    const [isLoading, setIsLoading] = useState(true);
    const [selectedResult, setSelectedResult] = useState(null);
    const [isReevaluating, setIsReevaluating] = useState(false);
    const evaluationLock = useRef(false);
    const [evaluationProgress, setEvaluationProgress] = useState(null); 
    const [isPublishing, setIsPublishing] = useState(false); 
    const [isDeleting, setIsDeleting] = useState(false);
    
    // Store actual questions here to handle migration (Firestore -> Storage)
    const [activeQuestions, setActiveQuestions] = useState(exam.questions || []);

    // Toppers State
    const [currentToppers, setCurrentToppers] = useState(exam.toppers || null);
    const [showToppersModal, setShowToppersModal] = useState(false);

    // Feedback Modal State
    const [gradingAttempt, setGradingAttempt] = useState(null); 

    // Filter and Sort State
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('dateDesc');

    // Sync state with prop if it changes externally
    useEffect(() => {
        if (exam.toppers) setCurrentToppers(exam.toppers);
    }, [exam.toppers]);

    // Effect to load questions if they are missing from the exam object (Migrated exams)
    useEffect(() => {
        const loadQuestions = async () => {
            if ((!exam.questions || exam.questions.length === 0) && exam.questionsUrl) {
                try {
                    const res = await fetch(exam.questionsUrl);
                    if (!res.ok) throw new Error("Failed to fetch questions file");
                    const data = await res.json();
                    setActiveQuestions(data);
                } catch (error) {
                    console.error("Failed to load questions from storage:", error);
                }
            } else if (exam.questions) {
                setActiveQuestions(exam.questions);
            }
        };
        loadQuestions();
    }, [exam]);

    // Function to fetch data
    const fetchAttemptsAndStudents = useCallback(async () => {
        setIsLoading(true);
        try {
            const attemptsQuery = query(
                collection(db, "attempts"),
                where("examId", "==", exam.id),
                where("submittedAt", "!=", null)
            );
            const attemptsSnapshot = await getDocs(attemptsQuery);
            const attemptsData = attemptsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setAttempts(attemptsData);

            const userIds = [...new Set(attemptsData.map(attempt => attempt.userId))];

            if (userIds.length > 0) {
                const studentsData = {};
                const chunks = [];
                for (let i = 0; i < userIds.length; i += 10) {
                    chunks.push(userIds.slice(i, i + 10));
                }
                
                for (const chunk of chunks) {
                    const usersQuery = query(collection(db, "users"), where(documentId(), "in", chunk));
                    const usersSnapshot = await getDocs(usersQuery);
                    usersSnapshot.docs.forEach(doc => {
                        studentsData[doc.id] = doc.data();
                    });
                }
                setStudents(studentsData);
            }
        } catch (error) {
            console.error("Error fetching attempts or students:", error);
            setModal({ show: true, message: `Error fetching submissions: ${error.message}` });
        } finally {
            setIsLoading(false);
        }
    }, [exam.id, setModal]);

    useEffect(() => {
        if (!exam || !exam.id) return;
        fetchAttemptsAndStudents();
    }, [exam.id, fetchAttemptsAndStudents]);

    // Derived state for Filtering & Sorting
    const processedAttempts = useMemo(() => {
        let filtered = [...attempts];

        if (searchQuery.trim() !== '') {
            const queryLower = searchQuery.toLowerCase();
            filtered = filtered.filter(attempt => {
                const student = students[attempt.userId] || {};
                const name = (student.name || '').toLowerCase();
                const email = (student.email || '').toLowerCase();
                return name.includes(queryLower) || email.includes(queryLower);
            });
        }

        if (filterStatus !== 'all') {
            filtered = filtered.filter(attempt => attempt.resubmissionStatus === filterStatus);
        }

        filtered.sort((a, b) => {
            const scoreA = getAttemptScores(a).totalScore;
            const scoreB = getAttemptScores(b).totalScore;
            const dateA = a.submittedAt ? a.submittedAt.toMillis() : 0;
            const dateB = b.submittedAt ? b.submittedAt.toMillis() : 0;

            switch (sortBy) {
                case 'scoreDesc': return scoreB - scoreA;
                case 'scoreAsc': return scoreA - scoreB;
                case 'dateAsc': return dateA - dateB;
                case 'dateDesc':
                default: return dateB - dateA;
            }
        });

        return filtered;
    }, [attempts, students, searchQuery, filterStatus, sortBy]);

    // --- LOGIC: Delete Submission ---
    const handleDeleteSubmission = async (attemptId) => {
        const confirmDelete = window.confirm(
            "Are you sure you want to permanently delete this submission? This action cannot be undone and the student will lose their result."
        );
        if (!confirmDelete) return;

        setIsDeleting(true);
        try {
            await deleteDoc(doc(db, 'attempts', attemptId));
            setAttempts(prev => prev.filter(a => a.id !== attemptId));
            setModal({ show: true, message: "Submission successfully deleted." });
        } catch (error) {
            console.error("Error deleting submission:", error);
            setModal({ show: true, message: `Failed to delete submission: ${error.message}` });
        } finally {
            setIsDeleting(false);
        }
    };

    // --- LOGIC: Publish Toppers ---
    const handlePublishToppers = async () => {
        if (!attempts || attempts.length === 0) {
            setModal({ show: true, message: "No submissions available to calculate ranks." });
            return;
        }

        const confirmPublish = window.confirm(
            "Are you sure you want to award Rank 1, 2, and 3? This will publish the results to all students assigned to this exam."
        );
        if (!confirmPublish) return;

        setIsPublishing(true);
        try {
            const scoredAttempts = attempts.map(attempt => {
                const student = students[attempt.userId] || { name: 'Unknown Student', email: 'N/A' };
                const totalScore = getAttemptScores(attempt).totalScore;
                return {
                    userId: attempt.userId,
                    name: student.name || 'Unknown',
                    email: student.email,
                    score: totalScore
                };
            });

            scoredAttempts.sort((a, b) => b.score - a.score);

            const toppers = scoredAttempts.slice(0, 3).map((ranker, index) => ({
                rank: index + 1,
                name: ranker.name,
                score: ranker.score,
                email: ranker.email
            }));

            const examRef = doc(db, 'exams', exam.id);
            await updateDoc(examRef, { toppers: toppers });

            setCurrentToppers(toppers);
            setShowToppersModal(true);
            setModal({ show: true, message: "🎉 Toppers (Rank 1, 2, 3) awarded and published successfully!" });
        } catch (error) {
            console.error("Error publishing toppers:", error);
            setModal({ show: true, message: `Failed to publish toppers: ${error.message}` });
        } finally {
            setIsPublishing(false);
        }
    };

    // --- LOGIC: Save Manual Feedback ---
    const handleSaveManualFeedback = async (attemptId, feedbackMap, evaluations) => {
        try {
            const attempt=gradingAttempt?.id===attemptId?gradingAttempt:attempts.find(item=>item.id===attemptId);
            if(!attempt)throw new Error('The submission is no longer available. Refresh before reviewing again.');
            const changes={adminFeedback:feedbackMap};
            if(evaluations) {
                const {aiScore,pendingCount}=getAttemptScores({evaluations});
                Object.assign(changes,{evaluations,aiGradedScore:aiScore,evaluationStatus:pendingCount?'pending':'complete'});
                if(!pendingCount)changes.resubmissionStatus='revalued';
            }
            await saveReviewedAttempt(attempt,changes);
            setAttempts(prev => prev.map(a => a.id === attemptId ? { ...a, ...changes } : a));
            setModal({ show: true, message: 'Marks and feedback saved successfully!' });
        } catch (error) {
            console.error("Error saving feedback:", error);
            throw error;
        }
    };

    // Only replace an approved revaluation after every answer has a confirmed mark.
    // A pending-only retry keeps successful original marks and can save partial recovery.
    const handleRevaluation = async (attempt, retryPendingOnly = false) => {
        if (evaluationLock.current) return;
        evaluationLock.current = true;
        setIsReevaluating(true);
        setEvaluationProgress({ id: attempt.id, completed: 0, total: 0 });
        try {
            let questionsToProcess = attempt.examQuestions || [];
            const targetUrl = attempt.questionsUrl || exam.publishedQuestionsUrl || exam.questionsUrl;
            if (!questionsToProcess.length && targetUrl) {
                questionsToProcess = await withEvaluationTimeout((async () => {
                    const res = await fetch(targetUrl);
                    if (!res.ok) throw new Error('Failed to load the saved exam version. Existing marks have been kept.');
                    return res.json();
                })(), 30000, 'The saved exam could not be loaded. Existing marks have been kept.');
            } else if (!questionsToProcess.length) {
                questionsToProcess = activeQuestions;
            }
            if (!Array.isArray(questionsToProcess) || !questionsToProcess.length) {
                throw new Error('Questions data is missing. Existing marks have been kept.');
            }
            const { pendingCount, ...grading } = await evaluateExamAnswers({
                questions: questionsToProcess, answers: attempt.answers || {}, examId: attempt.examId || exam.id,
                previousEvaluations: attempt.evaluations || {}, retryPendingOnly, strict: !retryPendingOnly,
                onProgress: progress => setEvaluationProgress({ id: attempt.id, ...progress }),
            });
            const changes = {
                ...grading,
                evaluationStatus: pendingCount ? 'pending' : 'complete',
                ...(pendingCount ? {} : { resubmissionStatus: 'revalued' }),
            };
            await saveReviewedAttempt(attempt,changes);
            setAttempts(previous => previous.map(item => item.id === attempt.id ? { ...item, ...changes } : item));
            setModal({ show: true, message: pendingCount
                ? `Confirmed marks saved. ${pendingCount} answer(s) still need review. You can retry these answers or use Grade & Feedback.`
                : 'Revaluation complete! Scores have been saved and the student result will update automatically.' });
        } catch (error) {
            console.error('Revaluation failed:', error);
            setModal({ show: true, message: `Revaluation failed: ${error.message}` });
        } finally {
            evaluationLock.current = false;
            setIsReevaluating(false);
            setEvaluationProgress(null);
        }
    };

    const handleReject = async (attemptId) => {
         try {
            const attemptRef = doc(db, 'attempts', attemptId);
            await updateDoc(attemptRef, { resubmissionStatus: 'rejected' });
            setAttempts(prev => prev.map(a => a.id === attemptId ? { ...a, resubmissionStatus: 'rejected' } : a));
            setModal({ show: true, message: "Revaluation request rejected." });
         } catch (e) {
             setModal({show:true, message: e.message});
         }
    };

    if (selectedResult) {
        return <ExamResult exam={exam} result={selectedResult} onBackToList={() => setSelectedResult(null)} setModal={setModal} />;
    }

    if (isLoading) return <div className="flex justify-center mt-8"><Spinner color="border-purple-500" /></div>;

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            {/* Render Toppers Modal */}
            {showToppersModal && (
                <ToppersModal 
                    toppers={currentToppers} 
                    examTitle={exam.title} 
                    onClose={() => setShowToppersModal(false)} 
                />
            )}

            {/* Render Feedback Modal */}
            {gradingAttempt && (
                <FeedbackModal 
                    attempt={gradingAttempt}
                    exam={exam}
                    studentName={students[gradingAttempt.userId]?.name || 'Unknown'}
                    onClose={() => setGradingAttempt(null)}
                    onSave={handleSaveManualFeedback}
                    questions={activeQuestions} 
                />
            )}

            <div className="flex justify-between items-center mb-4">
                <div>
                    <button onClick={onBack} className="text-sm text-purple-600 hover:underline mb-2">&larr; Back to Exams for {paper.name}</button>
                    <h2 className="text-2xl font-bold text-gray-800">Submissions for {exam.title}</h2>
                </div>
                
                <div className="flex gap-2">
                    {/* "View Rank List" Button */}
                    {currentToppers && (
                        <button 
                            onClick={() => setShowToppersModal(true)}
                            className="bg-purple-100 text-purple-700 border border-purple-300 font-bold py-2 px-4 rounded-lg hover:bg-purple-200 shadow-sm flex items-center gap-2"
                        >
                            <span>🏅</span> View Rank List
                        </button>
                    )}

                    {/* Button to award/re-award toppers */}
                    <button 
                        onClick={handlePublishToppers} 
                        disabled={isPublishing || attempts.length === 0}
                        className="bg-yellow-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-yellow-600 disabled:opacity-50 shadow-md flex items-center gap-2"
                    >
                        {isPublishing ? 'Publishing...' : (currentToppers ? '🔄 Recalculate Toppers' : '🏆 Award Toppers')}
                    </button>
                </div>
            </div>

            {/* NEW: Filter and Sort Toolbar */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6 flex flex-col md:flex-row gap-4 justify-between items-center border border-gray-200">
                <div className="w-full md:w-1/3">
                    <input 
                        type="text" 
                        placeholder="Search by student name or email..." 
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none"
                    />
                </div>
                <div className="flex w-full md:w-auto gap-4">
                    <select 
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none bg-white"
                    >
                        <option value="all">All Statuses</option>
                        <option value="revaluation_requested">Revaluation Requested</option>
                        <option value="revalued">Revalued</option>
                        <option value="rejected">Rejected</option>
                    </select>
                    
                    <select 
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:outline-none bg-white"
                    >
                        <option value="dateDesc">Newest First</option>
                        <option value="dateAsc">Oldest First</option>
                        <option value="scoreDesc">Highest Score</option>
                        <option value="scoreAsc">Lowest Score</option>
                    </select>
                </div>
            </div>

            <div className="space-y-4">
                {processedAttempts.length > 0 ? processedAttempts.map(attempt => {
                    const student = students[attempt.userId] || { name: 'Unknown Student', email: 'N/A' };
                    const { totalScore, totalPossible, pendingCount } = getAttemptScores(attempt);
                    
                    const isRevalRequested = attempt.resubmissionStatus === 'revaluation_requested';
                    const isRevalued = attempt.resubmissionStatus === 'revalued';

                    return (
                        <div key={attempt.id} className={`p-4 border rounded-lg flex flex-col md:flex-row justify-between items-center ${isRevalRequested ? 'bg-yellow-50 border-yellow-200' : 'bg-white'}`}>
                            <div>
                                <h3 className="font-bold text-lg">{student.name}</h3>
                                <p className="text-sm text-gray-600">{student.email}</p>
                                <p className="text-sm text-gray-600">Submitted: {attempt.submittedAt?.toDate().toLocaleString() || 'N/A'}</p>
                                {isRevalRequested && (
                                    <span className="inline-block mt-1 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-bold rounded">
                                        Revaluation Requested
                                    </span>
                                )}
                                {isRevalued && (
                                    <span className="inline-block mt-1 px-2 py-1 bg-green-100 text-green-800 text-xs font-bold rounded">
                                        Revalued
                                    </span>
                                )}
                            </div>
                            <div className="text-right mt-2 md:mt-0 flex flex-col items-end gap-2">
                                <p className="font-bold text-xl text-purple-600">{totalScore} / {totalPossible}</p>
                                {pendingCount > 0 && <p className="text-sm text-amber-700">Provisional: {pendingCount} answer(s) awaiting review</p>}
                                {evaluationProgress?.id === attempt.id && <p role="status" className="text-sm text-purple-700">
                                    {evaluationProgress.total ? `Evaluated ${evaluationProgress.completed} of ${evaluationProgress.total} answers…` : 'Preparing saved answers…'}
                                </p>}
                                <div className="flex gap-2 flex-wrap justify-end">
                                    {pendingCount > 0 && !isRevalRequested && <button
                                        onClick={() => handleRevaluation(attempt, true)} disabled={isReevaluating}
                                        className="bg-amber-100 text-amber-900 py-1 px-3 rounded-lg text-sm disabled:opacity-50"
                                    >Retry pending evaluation</button>}
                                    {isRevalRequested && (
                                        <>
                                            <button 
                                                onClick={() => handleRevaluation(attempt)}
                                                disabled={isReevaluating}
                                                className="bg-green-500 text-white py-1 px-3 rounded-lg hover:bg-green-600 text-sm disabled:opacity-50"
                                            >
                                                {evaluationProgress?.id === attempt.id ? 'Processing...' : 'Approve & Re-evaluate'}
                                            </button>
                                            <button 
                                                onClick={() => handleReject(attempt.id)}
                                                disabled={isReevaluating}
                                                className="bg-red-500 text-white py-1 px-3 rounded-lg hover:bg-red-600 text-sm disabled:opacity-50"
                                            >
                                                Reject
                                            </button>
                                        </>
                                    )}
                                    
                                    {/* Grading & Feedback Button */}
                                    <button
                                        onClick={() => setGradingAttempt(attempt)}
                                        disabled={isReevaluating}
                                        className="bg-purple-100 text-purple-700 py-1 px-3 rounded-lg hover:bg-purple-200 text-sm border border-purple-200 font-semibold"
                                    >
                                        📝 Grade & Feedback
                                    </button>

                                    {/* Allow Retake Button */}
                                    <button 
                                        onClick={() => onAllowRetake(attempt.id)}
                                        className="bg-indigo-100 text-indigo-700 py-1 px-3 rounded-lg hover:bg-indigo-200 text-sm border border-indigo-200 font-semibold"
                                        title="Allow student to take this exam again (1 attempt)"
                                    >
                                        Allow Retake
                                    </button>

                                    <button onClick={() => setSelectedResult(attempt)} className="bg-blue-500 text-white py-1 px-3 rounded-lg hover:bg-blue-600 text-sm">
                                        View Details
                                    </button>

                                    {/* NEW: Delete Button */}
                                    <button 
                                        onClick={() => handleDeleteSubmission(attempt.id)} 
                                        disabled={isDeleting || isReevaluating}
                                        className="bg-red-50 text-red-600 border border-red-200 py-1 px-3 rounded-lg hover:bg-red-100 text-sm font-semibold disabled:opacity-50 transition-colors"
                                        title="Permanently delete this submission"
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                }) : <p className="text-gray-500">No matching attempts found.</p>}
            </div>
        </div>
    );
};
export default ExamAttemptsList;